// Help guides and videos (user decision 2026-09-24: help content has no backend — it is static front-end content).
// Written from the Figma help frames (200:2796 guide article, 200:3491 guide video) and from how the v2 app works today.
// Only real features are described; anything still a preview in the app is called "coming soon".
// Pages: /help/guides/:slug (HelpGuide.tsx), /help/videos/:slug (HelpVideo.tsx); search: searchHelp below (HelpCentre).

import { POPULAR_GUIDES, TOPICS, VIDEOS } from './helpContent';

export const HELP_UPDATED = '21 Oct 2026';

export interface HelpLink { label: string; to: string }
export interface HelpStep {
    title: string;
    body: string;
    // caption of the screenshot frame under the step (the spec's grey "Screenshot / …" slot)
    screenshot?: string;
    // amber note under the step
    note?: string;
    link?: HelpLink;
}
export interface HelpFaq { q: string; a: string }
export interface HelpGuideContent {
    slug: string;
    title: string;
    // long title on the article page
    heading: string;
    topic: string;
    minutes: number;
    intro: string;
    before?: string;
    steps: HelpStep[];
    faqs: HelpFaq[];
    related: string[];
    video?: string;
    keywords: string;
}
export interface HelpChapter { title: string; time: string }
export interface HelpTranscriptLine { time: string; text: string }
export interface HelpVideoContent {
    slug: string;
    title: string;
    heading: string;
    topic: string;
    length: string;
    description: string;
    chapters: HelpChapter[];
    transcript: HelpTranscriptLine[];
    guide?: string;
    keywords: string;
}

// help centre topic cards (TOPICS in helpContent.ts) by title → url slug
export const TOPIC_SLUGS: { [title: string]: string } = {
    'Getting started': 'getting-started',
    'Share registry': 'share-registry',
    'Announcements': 'announcements',
    'Email campaigns': 'email-campaigns',
    'Website Hub': 'website-hub',
    'Account & security': 'account-security',
};
export const VIDEOS_TOPIC = 'videos';

export const topicTitle = (slug: string) => Object.keys(TOPIC_SLUGS).find(t => TOPIC_SLUGS[t] === slug);

export const GUIDES: HelpGuideContent[] = [
    // --- Getting started ---
    {
        slug: 'find-your-way-around',
        title: 'Find your way around Investi',
        heading: 'Find your way around Investi',
        topic: 'Getting started',
        minutes: 3,
        intro: 'A quick map of the app: the top menu, the client switcher and what the Dashboard shows you each morning.',
        steps: [
            { title: 'Use the top menu', body: 'The dark bar at the top of every page has the main areas: Dashboard, Share Registry, Communications (a menu with Announcements, Email Campaigns, Contacts, Email Templates, Social Posts and Investor Questions) and Website Hub.', screenshot: 'Top menu with Dashboard, Share Registry, Communications and Website Hub', link: { label: 'Open the Dashboard', to: '/dashboard/overview' } },
            { title: 'Pick the client you are working on', body: 'If you look after more than one company, the client switcher at the top right shows the one you are working on. Click it, search by name or ticker and choose another client — every page then shows that company’s data. My Account › My Clients lists them all.', screenshot: 'Client switcher open with a search box', link: { label: 'Open My Clients', to: '/account/clients' } },
            { title: 'Read the Dashboard', body: 'The Dashboard brings together what needs your attention (emails to confirm, investor questions waiting for an answer), the share price, top shareholders, recent announcements and the latest activity from investors. Each card links to the page with the detail.' },
            { title: 'Get help on any page', body: 'The ? button in the top bar opens Help & guides beside the page you are on, with popular guides, a search box and text size. “Open full help centre” brings you here.' },
        ],
        faqs: [
            { q: 'Why can’t I see a menu item a colleague can?', a: 'What you see depends on your access to that client. Ask an admin for your company, or email support, if you need more access.' },
            { q: 'Can I make the text bigger?', a: 'Yes. Choose a larger “A” under Text size in Help & guides, or in My Account › My Profile, where the choice is saved for next time.' },
        ],
        related: ['change-your-profile-and-text-size', 'invite-a-teammate', 'read-the-holdings-movement-report'],
        video: 'investi-in-10-minutes',
        keywords: 'start new navigation menu dashboard client switcher switch company overview',
    },
    {
        slug: 'invite-a-teammate',
        title: 'Invite a teammate',
        heading: 'Invite a teammate to your company',
        topic: 'Getting started',
        minutes: 2,
        intro: 'How to give a colleague, director or adviser their own login for your company.',
        steps: [
            { title: 'Email us who to add', body: 'Inviting people yourself from Client settings › Users & Access is coming soon. Until then, email support with the person’s name, email address and what they need to do (for example: approve announcement emails, answer investor questions, read the registry). We set it up the same business day.', link: { label: 'Open Users & Access', to: '/client-settings/users' } },
            { title: 'They set their password', body: 'Your teammate gets an email with a link to sign in. The first time they sign in on a new device, Investi emails them a 6‑digit code to confirm it is really them.' },
            { title: 'Add them as an approver if needed', body: 'If they should approve announcement emails, add their email under Client settings › Announcement Settings › Rules, in “Who Approves and Gets Confirmations”.', link: { label: 'Open Announcement Settings', to: '/client-settings/announcements' } },
        ],
        faqs: [
            { q: 'Does each person need their own login?', a: 'Yes — please never share a login. Each person’s actions, such as approving an email, are recorded against their own account.' },
            { q: 'How do I remove someone who has left?', a: 'Email support and we remove their access straight away. Removing people from Users & Access yourself is coming soon.' },
        ],
        related: ['set-up-auto-send-rules', 'sign-in-codes-and-two-factor', 'find-your-way-around'],
        keywords: 'invite teammate colleague user add person access login director team members roles',
    },

    // --- Share registry ---
    {
        slug: 'read-the-holdings-movement-report',
        title: 'Read the holdings movement report',
        heading: 'Read the holdings movement report',
        topic: 'Share registry',
        minutes: 3,
        intro: 'The holdings movement report shows who bought and sold on each trading day, by comparing that day’s register with the day before.',
        before: 'Movement reports need your share registry to be connected. If the Holdings movement tab is empty, email support and we’ll check the connection with your registry.',
        steps: [
            { title: 'Open Holdings movement', body: 'In the top menu choose Share Registry, then the Holdings movement tab. The top card shows the latest report at a glance: how many holders moved, shares bought and sold, and the largest moves.', screenshot: 'Share Registry › Holdings movement', link: { label: 'Open Holdings movement', to: '/share-registry/holdings-movement' } },
            { title: 'Pick a day', body: 'Daily Movement Reports lists every report, newest first. Click a day to open its full report: what happened on the market, every holder whose balance changed, and Previous Day / Next Day to step through the week.', screenshot: 'One day’s movement report with all movements' },
            { title: 'Read a movement', body: 'Each row shows the holder, their balance before and after, and the change. Green is a buy, red is a sell. A holder who appears for the first time is a new holder; one whose balance falls to zero has sold out.' },
            { title: 'Run or download a report', body: 'To check a day that isn’t in the list, choose a date under “Run a Movement Report”. Download Report saves the report so you can share it with the board. Emailing a report straight from Investi is coming soon — download it and attach it for now.' },
        ],
        faqs: [
            { q: 'Why is there no report for today?', a: 'The register for a trading day is usually available from the registry the next morning, so the newest report is normally yesterday’s.' },
            { q: 'Who is a “holder” here?', a: 'Each holding on the register — one person or entity can have more than one holding, for example a personal holding and a super fund.' },
        ],
        related: ['search-holders', 'check-your-top-holders', 'make-a-registry-report'],
        video: 'reading-a-movement-report',
        keywords: 'holdings movement report registry buy sell daily changes shareholders holders report',
    },
    {
        slug: 'search-holders',
        title: 'Search holders and see their history',
        heading: 'Search your holders and see each holder’s history',
        topic: 'Share registry',
        minutes: 3,
        intro: 'Find any shareholder on the register and see their holding, movements and contact with you over time.',
        steps: [
            { title: 'Open All holders', body: 'Share Registry opens on All holders: every holder with a positive balance, largest first. Type a name or holder number in the search box, or use the filters to narrow the list.', link: { label: 'Open All holders', to: '/share-registry' } },
            { title: 'Open a holder', body: 'Click a row to open the holder. You’ll see their current balance and rank, a chart and table of their movements, and recent activity such as emails they opened. Use Previous / Next to move through the register.', screenshot: 'Holder detail with balance chart and movements' },
            { title: 'Export a holder report', body: 'Export Holder Report gives you a printable PDF or a CSV of that holder’s movements — useful for a board paper or an adviser.' },
        ],
        faqs: [
            { q: 'Can I add a holder to my email contacts?', a: 'Adding a holder to your contacts from their page is coming soon. Holders with an email on the register are already on the Shareholders list in Contacts.' },
            { q: 'Why don’t I see option holders?', a: 'All holders lists the main share class. A holder’s page shows every security they hold.' },
        ],
        related: ['read-the-holdings-movement-report', 'check-your-top-holders', 'manage-contacts-and-lists'],
        keywords: 'search holder shareholder find investor register history export holder report',
    },
    {
        slug: 'check-your-top-holders',
        title: 'Check your top 20 holders',
        heading: 'Check your top 20 holders and who is moving',
        topic: 'Share registry',
        minutes: 2,
        intro: 'See who owns the company, how concentrated the register is and the biggest moves this month.',
        steps: [
            { title: 'Open Top holders', body: 'Share Registry › Top holders lists your largest holders with their balance and percentage of the company, and groups them so you can see what the top 20 and top 50 hold together.', link: { label: 'Open Top holders', to: '/share-registry/top-holders' } },
            { title: 'Look at the biggest moves', body: '“Biggest moves this month” shows which large holders have bought or sold the most since the start of the month.' },
            { title: 'Download the list', body: 'Download This List saves the top holders for your records or the board pack.' },
        ],
        faqs: [
            { q: 'Why can my top 20 show more than 20 names?', a: 'Holders with exactly the same balance share a position, so ties can push the list past 20.' },
        ],
        related: ['make-a-registry-report', 'read-the-holdings-movement-report', 'search-holders'],
        keywords: 'top 20 holders top 50 largest shareholders concentration biggest moves holdings report',
    },
    {
        slug: 'make-a-registry-report',
        title: 'Make a registry report for the board',
        heading: 'Make a registry report for the board',
        topic: 'Share registry',
        minutes: 2,
        intro: 'Ready-made registry reports, such as the Top 20 and the monthly board pack, that you can print or save as a PDF.',
        steps: [
            { title: 'Open Reports', body: 'Share Registry › Reports shows the reports you can make, with a short note on what each is for.', link: { label: 'Open Reports', to: '/share-registry/reports' } },
            { title: 'Make the report', body: 'Click Make This Report, choose the options (for example whether to include holder contact details — keep that file secure) and download it. The report opens as a printable page you can save as a PDF.', screenshot: 'Make this report options' },
            { title: 'Find it again', body: 'Reports You’ve Made lists what you have made recently so you can download it again. Having a report emailed to you every month is coming soon.' },
        ],
        faqs: [
            { q: 'Can I email the report from Investi?', a: 'Not yet — that’s coming soon. Download the PDF and attach it to your email.' },
        ],
        related: ['check-your-top-holders', 'read-the-holdings-movement-report'],
        keywords: 'registry report board pack top 20 pdf print download monthly report',
    },

    // --- Announcements ---
    {
        slug: 'add-an-asx-announcement-and-email-it',
        title: 'Add an ASX announcement and email it',
        heading: 'Add an ASX announcement and email it to investors',
        topic: 'Announcements',
        minutes: 4,
        intro: 'This guide walks you through adding an announcement PDF, setting whether it is price sensitive, and sending (or approving) the email that goes to your subscribers. It takes about two minutes once you’ve done it a few times.',
        before: 'For ASX-listed clients, Investi picks up new announcements from the ASX by itself — you only need to add one by hand if it is missing or isn’t lodged with the ASX. You’ll need the announcement PDF, and to know whether it was marked price sensitive when it was lodged.',
        steps: [
            { title: 'Open Announcements', body: 'In the top menu, choose Communications, then Announcements. You’ll see every announcement for the client you’re working on, newest first. Use the client switcher at the top right if you need a different company.', screenshot: 'Communications menu → Announcements', link: { label: 'Open Announcements', to: '/communications' } },
            { title: 'Add the announcement', body: 'Click the cyan “+ Add Announcement” button. A panel slides in from the right. Type the headline exactly as it was lodged with the ASX, check the date and time (Sydney time), then drag the PDF into the box or click “browse files”. The PDF can be up to 25 MB.', screenshot: 'Add announcement panel with headline, date and PDF drop zone', note: 'Investi makes the thumbnail from page 1 of the PDF. Choosing a different thumbnail page is coming soon.' },
            { title: 'Choose price sensitivity', body: 'Turn on “Price sensitive” if the ASX marked it that way. This matters: your client’s rules decide which announcements are emailed to subscribers automatically and which wait for an approver. You can change these rules under Client settings › Announcement Settings › Rules.', link: { label: 'Open the rules', to: '/client-settings/announcements' } },
            { title: 'Review the email', body: 'Click Save Announcement. Investi drafts the email with your announcement template and a short summary of the PDF written by AI. Click the headline in the list to open the announcement and choose Email Preview. Check the summary reads well, then use “Send a Test to Me”.', screenshot: 'Email preview with the AI summary' },
            { title: 'Send or approve', body: 'If the rules email this kind of announcement automatically, nothing more is needed. Otherwise the announcement shows “Confirm to send” in the list, and an approver opens it and clicks “Approve and Send”. Opens and clicks then appear under Communications › Email Campaigns.' },
        ],
        faqs: [
            { q: 'What if I upload the wrong PDF?', a: 'Open the announcement from the list, choose Edit and use Replace PDF, or delete the announcement from the ⋯ menu and add it again. If the email has already gone, email support and we’ll help you send a correction.' },
            { q: 'Who receives the email?', a: 'Everyone subscribed to the client’s announcement emails — the default list in Contacts. People who unsubscribed are never emailed.' },
            { q: 'Can I see who read it?', a: 'Yes. Open the announcement, or the campaign under Email Campaigns, and choose Statistics for opens, clicks and views.' },
        ],
        related: ['approve-an-announcement-email', 'change-the-announcement-email-template', 'set-up-auto-send-rules'],
        video: 'emailing-an-announcement',
        keywords: 'add announcement upload pdf asx email investors price sensitive send subscribers',
    },
    {
        slug: 'approve-an-announcement-email',
        title: 'Approve an announcement email',
        heading: 'Approve an announcement email',
        topic: 'Announcements',
        minutes: 2,
        intro: 'When an announcement isn’t emailed automatically, an approver checks the email and sends it. Here’s how.',
        steps: [
            { title: 'Find what is waiting', body: 'The Dashboard lists “Confirm the email for …” items at the top. In Communications › Announcements, the “Waiting for approval” tile shows how many are waiting, and the Email filter has a “Confirm to send” option.', link: { label: 'Open Announcements', to: '/communications' } },
            { title: 'Check the email', body: 'Click the headline, then Email Preview. It shows who it goes to, the template, the price sensitive flag and the email exactly as investors will see it, including the AI summary.', screenshot: 'Email preview waiting for approval' },
            { title: 'Test, then approve', body: 'Click “Send a Test to Me” to see it in your own inbox. When you’re happy, click “Approve and Send”. The email goes to subscribers straight away.' },
        ],
        faqs: [
            { q: 'Who can approve?', a: 'People listed in Client settings › Announcement Settings › Rules under “Who Approves and Gets Confirmations”, with permission to edit announcements.' },
            { q: 'Can I stop an email after approving it?', a: 'No — Approve and Send emails subscribers immediately, so always send yourself a test first.' },
        ],
        related: ['add-an-asx-announcement-and-email-it', 'set-up-auto-send-rules', 'change-the-announcement-email-template'],
        keywords: 'approve announcement email confirm to send approver waiting approval test',
    },
    {
        slug: 'set-up-auto-send-rules',
        title: 'Set up auto‑send rules',
        heading: 'Choose which announcements are emailed automatically',
        topic: 'Announcements',
        minutes: 3,
        intro: 'Rules decide what happens when an announcement arrives: email subscribers straight away, or wait for an approver.',
        steps: [
            { title: 'Open the rules', body: 'Open the client switcher, click Settings next to the client, then Announcement Settings. Rules is the first section.', link: { label: 'Open Announcement Settings', to: '/client-settings/announcements' } },
            { title: 'Set the two groups', body: '“When an Announcement Arrives” has one group for price‑sensitive announcements and one for all others. For each, choose “Email subscribers automatically” and whether to “Send confirmation to approvers”.', screenshot: 'Rules: price sensitive and other announcements' },
            { title: 'Skip routine announcements', body: 'Under “Don’t Auto‑Send These”, add words or phrases — for example “Appendix 3Y”. Announcements with these words in the headline are never emailed automatically. “Insert Standard Filter” adds a ready-made list of routine ASX forms.' },
            { title: 'Choose approvers', body: 'Add the people who approve emails and get confirmations under “Who Approves and Gets Confirmations”, then save.' },
        ],
        faqs: [
            { q: 'Does a change apply to announcements already in the list?', a: 'No — rules apply to announcements that arrive after you save.' },
        ],
        related: ['approve-an-announcement-email', 'add-an-asx-announcement-and-email-it'],
        keywords: 'auto send rules automatic email price sensitive filter words approvers settings',
    },
    {
        slug: 'change-the-announcement-email-template',
        title: 'Change the announcement email template',
        heading: 'Change the email template used for announcements',
        topic: 'Announcements',
        minutes: 3,
        intro: 'Every announcement email is built from one template. Here’s how to choose or edit it.',
        steps: [
            { title: 'Open Email Templates', body: 'Communications › Email Templates shows your templates and which one each kind of email uses.', link: { label: 'Open Email Templates', to: '/communications/email-templates' } },
            { title: 'Choose the template', body: 'On the ASX Announcements card click Change, pick a template and save. New announcement emails use it from then on.', screenshot: 'Set template for a role' },
            { title: 'Edit and test', body: 'Open a template to change its logo, colours and text in the builder, then use Send a Test to see it filled with your newest announcement.' },
        ],
        faqs: [
            { q: 'Can I start from a copy?', a: 'Yes — copy an existing template from its menu, then edit the copy so the original stays as it is.' },
        ],
        related: ['add-an-asx-announcement-and-email-it', 'send-an-email-campaign'],
        keywords: 'email template announcement design logo branding change template builder',
    },

    // --- Email campaigns ---
    {
        slug: 'send-an-email-campaign',
        title: 'Send an email campaign',
        heading: 'Write and send an email campaign',
        topic: 'Email campaigns',
        minutes: 4,
        intro: 'Send a news update, AGM notice or investor newsletter to one or more of your contact lists.',
        steps: [
            { title: 'Start a new campaign', body: 'Open Communications › Email Campaigns and click New Campaign.', link: { label: 'Open Email Campaigns', to: '/communications/email-campaigns' } },
            { title: 'Subject, audience and template', body: 'Write the subject line, choose who receives it (one or more lists — the counts show how many subscribed contacts each has) and pick a template. Click Save and Continue.', screenshot: 'New campaign: subject, lists and template' },
            { title: 'Write the email', body: 'The email builder opens with your template. Edit the text and images; your draft saves as you go.' },
            { title: 'Test, then send', body: 'Click Send a Test to send it to up to five addresses. When it looks right, click Send and confirm with Send Now. Scheduling for later is coming soon.' },
        ],
        faqs: [
            { q: 'Can I cancel after Send Now?', a: 'No — it goes out straight away, so always send a test first.' },
            { q: 'Where do I see the results?', a: 'Click the campaign in Email Campaigns and choose Statistics for opens, clicks and who read it.' },
        ],
        related: ['read-campaign-statistics', 'manage-contacts-and-lists', 'change-the-announcement-email-template'],
        keywords: 'email campaign newsletter send news update subscribers lists test',
    },
    {
        slug: 'read-campaign-statistics',
        title: 'Read campaign statistics',
        heading: 'See who opened and clicked your email',
        topic: 'Email campaigns',
        minutes: 2,
        intro: 'Each sent campaign — announcement emails included — has a statistics page.',
        steps: [
            { title: 'Open the campaign', body: 'In Communications › Email Campaigns, click a sent campaign.', link: { label: 'Open Email Campaigns', to: '/communications/email-campaigns' } },
            { title: 'Choose Statistics', body: 'Statistics shows delivered, opened and clicked, opens over time, which links were clicked and who read it.', screenshot: 'Campaign statistics' },
        ],
        faqs: [
            { q: 'Why is the open rate sometimes high straight after sending?', a: 'Some company mail systems open and scan every email automatically, which can count as an open.' },
        ],
        related: ['send-an-email-campaign', 'add-an-asx-announcement-and-email-it'],
        keywords: 'campaign statistics open rate clicks report results who read',
    },
    {
        slug: 'manage-contacts-and-lists',
        title: 'Find contacts and lists',
        heading: 'Find your contacts, lists and segments',
        topic: 'Email campaigns',
        minutes: 2,
        intro: 'Contacts holds everyone who receives your emails, grouped into lists and segments.',
        steps: [
            { title: 'Open Contacts', body: 'Communications › Contacts lists every contact with their status. Pick a list or segment on the left to narrow it, or search by name or email.', link: { label: 'Open Contacts', to: '/communications/contacts' } },
            { title: 'Open a contact', body: 'Click a contact to see their details, the lists they’re on and the emails they’ve received.' },
            { title: 'Adding and importing', body: 'Adding contacts, importing a CSV and creating lists in Investi are coming soon. Investors can already sign up through the subscribe form on your website, and holders with an email on the register are added to the Shareholders list.' },
        ],
        faqs: [
            { q: 'Can I export my contacts?', a: 'Yes — use Export on the Contacts page.' },
        ],
        related: ['send-an-email-campaign', 'search-holders'],
        keywords: 'contacts lists segments subscribers import csv shareholders list',
    },

    // --- Website Hub ---
    {
        slug: 'answer-an-investor-question-safely',
        title: 'Answer an investor question safely',
        heading: 'Answer an investor question safely',
        topic: 'Website Hub',
        minutes: 4,
        intro: 'Investors can ask questions on your announcements. This guide shows how to answer — and how to stay within your continuous‑disclosure obligations while you do.',
        before: 'Continuous‑disclosure checklist: only answer with information that is already public (in an announcement, report or presentation). Never give new price‑sensitive information, forecasts or advice. If in doubt, say you can’t comment and point to the relevant announcement — and check with your company secretary.',
        steps: [
            { title: 'Open Investor Questions', body: 'Communications › Investor Questions lists questions waiting for an answer, with the announcement each one was asked on. The Dashboard also shows how many are waiting.', link: { label: 'Open Investor Questions', to: '/communications/investor-questions' } },
            { title: 'Read the question', body: 'Click a question to see it in full, who asked it, and the announcement it relates to.', screenshot: 'Investor question with the answer box' },
            { title: 'Write the answer', body: 'Type your answer, or click “Draft with AI” for a first draft based on the announcement — always read and edit it. Save Draft keeps it for later or for a colleague to check.', note: 'An AI draft can be wrong. Check every fact against the announcement before you send it.' },
            { title: 'Answer privately or publish', body: '“Answer Privately” replies only to the investor. “Answer & Publish” also shows the question and answer publicly. If the investor asked for a private reply, Publish is turned off.' },
            { title: 'Check what is published', body: 'Published Answers lists every public answer.', link: { label: 'Open Published Answers', to: '/communications/investor-questions/published' } },
        ],
        faqs: [
            { q: 'Should I answer every question?', a: 'No. It’s fine — and often safest — to reply that you can’t comment beyond what has been announced.' },
            { q: 'Can someone else review my answer first?', a: 'Save Draft, and ask them to open the question. Assigning a question to a colleague in Investi is coming soon.' },
        ],
        related: ['add-an-asx-announcement-and-email-it', 'post-an-announcement-to-x'],
        keywords: 'answer investor question comment reply publish private continuous disclosure ai draft',
    },
    {
        slug: 'post-an-announcement-to-x',
        title: 'Post an announcement to X',
        heading: 'Post an announcement to X',
        topic: 'Website Hub',
        minutes: 2,
        intro: 'Share an announcement on your company’s X account without leaving Investi.',
        steps: [
            { title: 'Connect your X account', body: 'Open Client settings › Announcement Settings › Social Posts and connect the company’s X account. Communications › Social Posts then shows it as connected.', link: { label: 'Open Social Posts settings', to: '/client-settings/announcements/social' } },
            { title: 'Create the post', body: 'In Announcements, open the ⋯ menu on an announcement and choose Create a Social Post. Investi writes a draft with the headline, a link and your ticker — edit it, then post.', screenshot: 'New social post with preview' },
        ],
        faqs: [
            { q: 'Can announcements be posted automatically?', a: 'Automatic posting, and LinkedIn, are coming soon.' },
        ],
        related: ['add-an-asx-announcement-and-email-it', 'answer-an-investor-question-safely'],
        keywords: 'social post x twitter linkedin share announcement',
    },

    // --- Account & security ---
    {
        slug: 'sign-in-codes-and-two-factor',
        title: 'Sign-in codes and two‑factor',
        heading: 'Sign-in codes and two‑factor authentication',
        topic: 'Account & security',
        minutes: 2,
        intro: 'How Investi checks it’s really you when you sign in.',
        steps: [
            { title: 'Sign in with your email code', body: 'When you sign in on a new device, Investi emails you a 6‑digit code after your password. Type it in within 10 minutes. If it doesn’t arrive, check your junk folder or click to resend.' },
            { title: 'Authenticator apps are coming soon', body: 'Two‑factor with an authenticator app, backup codes, and signing out other devices from My Account › Account & Security are coming soon. Until then the emailed code protects every new device.', link: { label: 'Open Account & Security', to: '/account/security' } },
        ],
        faqs: [
            { q: 'I forgot my password', a: 'Click “Forgot password?” on the sign-in page and we’ll email you a link to set a new one.' },
        ],
        related: ['change-your-profile-and-text-size', 'invite-a-teammate'],
        keywords: 'two factor 2fa sign in code login security password authenticator device',
    },
    {
        slug: 'change-your-profile-and-text-size',
        title: 'Change your profile and text size',
        heading: 'Change your profile, photo and text size',
        topic: 'Account & security',
        minutes: 2,
        intro: 'Keep your name, photo and contact details up to date, and make the app easier to read.',
        steps: [
            { title: 'Open My Profile', body: 'Click your initials at the top right and choose My Profile.', link: { label: 'Open My Profile', to: '/account' } },
            { title: 'Update your details', body: 'Change your name, photo (JPG or PNG up to 5 MB) or email address, then save. Changing your email asks you to confirm the new address.' },
            { title: 'Choose a text size', body: 'Pick Standard, Large or Extra large. The whole app grows to match, and it’s saved for next time.' },
        ],
        faqs: [
            { q: 'Can I change the text size just for now?', a: 'Yes — the A buttons in Help & guides change it straight away without saving.' },
        ],
        related: ['sign-in-codes-and-two-factor', 'find-your-way-around'],
        keywords: 'profile photo name email text size bigger font accessibility account',
    },
];

export const HELP_VIDEOS: HelpVideoContent[] = [
    {
        slug: 'investi-in-10-minutes',
        title: 'Investi in 10 minutes',
        heading: 'Investi in 10 minutes — a tour for new users',
        topic: 'Getting started',
        length: '9:48',
        description: 'A calm walk‑through of the whole platform for someone who has never used it: switching clients, reading the registry, adding an announcement, sending an email campaign and answering investor questions. No prior knowledge needed.',
        chapters: [
            { title: 'Welcome & switching clients', time: '0:00' },
            { title: 'Reading the share registry', time: '1:12' },
            { title: 'Adding an announcement', time: '2:40' },
            { title: 'Sending an email campaign', time: '5:05' },
            { title: 'Answering investor questions', time: '7:10' },
            { title: 'Where to get help', time: '9:02' },
        ],
        transcript: [
            { time: '0:00', text: 'Welcome to Investi. In the next ten minutes we’ll go through the things you’ll do most often.' },
            { time: '0:31', text: 'If you look after more than one company, the client switcher at the top right shows which one you’re working on. Click it to change.' },
            { time: '1:12', text: 'Share Registry shows everyone on your register. All holders is the full list, largest first — click anyone to see their history.' },
            { time: '1:58', text: 'Holdings movement compares each day’s register with the day before, so you can see who bought and who sold.' },
            { time: '2:40', text: 'Now announcements. ASX announcements arrive by themselves, but you can add one by hand with “Add Announcement”.' },
            { time: '3:29', text: 'Once the PDF is in, Investi reads it and writes a short plain‑English summary for the email.' },
            { time: '3:42', text: 'Now — this is the important bit — the price‑sensitive switch. If the ASX marked it price sensitive, turn this on.' },
            { time: '3:58', text: 'Your client’s rules decide what happens next: some announcements are emailed automatically, others wait for an approver to confirm them…' },
            { time: '4:15', text: '…and that’s how the approval step keeps you compliant, without slowing down the announcements that need to go now.' },
            { time: '5:05', text: 'For anything else — a newsletter, an AGM notice — use Email Campaigns. Choose a subject, your lists and a template, then write the email.' },
            { time: '6:20', text: 'Always send yourself a test. When it looks right, Send Now emails your lists straight away.' },
            { time: '7:10', text: 'Investors can ask questions on your announcements. You’ll find them in Investor Questions.' },
            { time: '8:05', text: 'Answer privately, or publish the answer for everyone — and only ever use information that’s already public.' },
            { time: '9:02', text: 'If you get stuck, the question mark at the top of every page opens help for the page you’re on. Thanks for watching.' },
        ],
        guide: 'find-your-way-around',
        keywords: 'tour introduction overview new user getting started walkthrough',
    },
    {
        slug: 'emailing-an-announcement',
        title: 'Emailing an announcement',
        heading: 'Emailing an announcement to your investors',
        topic: 'Announcements',
        length: '3:12',
        description: 'From adding the announcement PDF to the email arriving in your investors’ inboxes: price sensitivity, the automatic rules, the email preview and approving.',
        chapters: [
            { title: 'Adding the announcement', time: '0:00' },
            { title: 'Price sensitive or not', time: '0:48' },
            { title: 'Checking the email', time: '1:35' },
            { title: 'Approving and sending', time: '2:20' },
        ],
        transcript: [
            { time: '0:00', text: 'Open Communications, then Announcements, and click Add Announcement.' },
            { time: '0:14', text: 'Type the headline exactly as it was lodged, check the date and time — that’s Sydney time — and drop in the PDF.' },
            { time: '0:48', text: 'If the ASX marked it price sensitive, turn on the price sensitive switch.' },
            { time: '1:05', text: 'Your rules, under Client settings, decide whether it’s emailed automatically or waits for an approver.' },
            { time: '1:35', text: 'Click the headline to open the email preview. You’ll see the AI summary and exactly what investors will get.' },
            { time: '1:58', text: 'Send a Test to Me puts a copy in your own inbox.' },
            { time: '2:20', text: 'If it’s waiting for approval, click Approve and Send. It goes out straight away.' },
            { time: '2:51', text: 'Opens and clicks then show under Email Campaigns, in Statistics.' },
        ],
        guide: 'add-an-asx-announcement-and-email-it',
        keywords: 'announcement email approve send price sensitive',
    },
    {
        slug: 'reading-a-movement-report',
        title: 'Reading a movement report',
        heading: 'Reading a holdings movement report',
        topic: 'Share registry',
        length: '4:05',
        description: 'What the holdings movement report shows, how to read a day’s movements, and how to step back through earlier days.',
        chapters: [
            { title: 'Where to find it', time: '0:00' },
            { title: 'The day at a glance', time: '0:40' },
            { title: 'Reading each movement', time: '1:45' },
            { title: 'Earlier days and downloads', time: '3:10' },
        ],
        transcript: [
            { time: '0:00', text: 'Open Share Registry and choose the Holdings movement tab.' },
            { time: '0:40', text: 'The top card is the latest report at a glance: how many holders moved, and the largest buys and sells.' },
            { time: '1:20', text: 'Below it, Daily Movement Reports lists every day. Click one to open it.' },
            { time: '1:45', text: 'Each row is a holder whose balance changed: before, after and the difference. Green is a buy, red is a sell.' },
            { time: '2:30', text: 'Someone appearing for the first time is a new holder; a balance that drops to zero means they’ve sold out.' },
            { time: '3:10', text: 'Use Previous Day and Next Day to step through the week, and Download Report to share it with the board.' },
        ],
        guide: 'read-the-holdings-movement-report',
        keywords: 'movement report registry holders buy sell daily',
    },
];

export const findGuide = (slug: string) => GUIDES.find(g => g.slug === slug);
export const findVideo = (slug: string) => HELP_VIDEOS.find(v => v.slug === slug);
export const guideHref = (slug: string) => `/help/guides/${slug}`;
export const videoHref = (slug: string) => `/help/videos/${slug}`;
export const topicHref = (topic: string) => `/help?topic=${TOPIC_SLUGS[topic] ?? topic}`;

// the titles shown in helpContent.ts (popular guides, video tiles) → their page
export const hrefForTitle = (title: string) => {
    const plain = title.replace(/^Watch:\s*/, '');
    const guide = GUIDES.find(g => g.title === plain);
    if (guide) return guideHref(guide.slug);
    const video = HELP_VIDEOS.find(v => v.title === plain);
    if (video) return videoHref(video.slug);
    return `/help?q=${encodeURIComponent(plain)}`;
};

export const guidesInTopic = (topic: string) => GUIDES.filter(g => g.topic === topic);

export interface HelpResult { kind: 'guide' | 'video'; slug: string; title: string; meta: string; topic: string; summary: string; href: string }

const guideResult = (g: HelpGuideContent): HelpResult =>
    ({ kind: 'guide', slug: g.slug, title: g.title, meta: `${g.minutes} min read`, topic: g.topic, summary: g.intro, href: guideHref(g.slug) });
const videoResult = (v: HelpVideoContent): HelpResult =>
    ({ kind: 'video', slug: v.slug, title: v.title, meta: `Video · ${v.length}`, topic: v.topic, summary: v.description, href: videoHref(v.slug) });

export const topicResults = (topicSlug: string): HelpResult[] => {
    if (topicSlug === VIDEOS_TOPIC) return HELP_VIDEOS.map(videoResult);
    const topic = topicTitle(topicSlug);
    if (!topic) return [];
    return GUIDES.filter(g => g.topic === topic).map(guideResult)
        .concat(HELP_VIDEOS.filter(v => v.topic === topic).map(videoResult));
};

const words = (s: string) => s.toLowerCase().replace(/[‑–—]/g, '-').split(/[^a-z0-9-]+/).filter(w => w.length > 1);
const STOP = ['a', 'an', 'the', 'to', 'do', 'how', 'my', 'and', 'or', 'of', 'in', 'on', 'for', 'is', 'it', 'can', 'what', 'where', 'with'];

// client-side search: every query word (stop words aside) must appear somewhere; title hits rank first
export const searchHelp = (query: string): HelpResult[] => {
    const terms = words(query).filter(w => !STOP.includes(w));
    if (!terms.length) return [];
    const score = (title: string, rest: string) => {
        const t = title.toLowerCase();
        const r = rest.toLowerCase();
        let total = 0;
        for (const term of terms) {
            // "announcements" finds "announcement" and the other way round
            const stem = term.length > 4 ? term.replace(/(es|s)$/, '') : term;
            if (t.includes(stem)) total += 3;
            else if (r.includes(stem)) total += 1;
            else return 0;
        }
        return total;
    };
    const scored = GUIDES.map(g => ({ r: guideResult(g), s: score(g.title, [g.heading, g.topic, g.intro, g.keywords, g.before ?? '', ...g.steps.map(x => `${x.title} ${x.body}`), ...g.faqs.map(f => `${f.q} ${f.a}`)].join(' ')) }))
        .concat(HELP_VIDEOS.map(v => ({ r: videoResult(v), s: score(v.title, [v.heading, v.topic, v.description, v.keywords, ...v.chapters.map(c => c.title), ...v.transcript.map(l => l.text)].join(' ')) })));
    return scored.filter(x => x.s > 0).sort((a, b) => b.s - a.s).map(x => x.r);
};

// every title in helpContent.ts has a page (checked at load in development)
if (process.env.NODE_ENV === 'development') {
    const missing = POPULAR_GUIDES.map(g => g.title).concat(VIDEOS.map(v => v.title))
        .filter(t => hrefForTitle(t).startsWith('/help?'));
    const topicsMissing = TOPICS.filter(t => !TOPIC_SLUGS[t.title]).map(t => t.title);
    if (missing.length || topicsMissing.length) console.warn('Help content without a page:', missing, topicsMissing);
}
